// src/components/MapComponent.jsx
import React, { useContext, useEffect, useRef, useState } from 'react';
import { MapContext } from '../context/MapContext';
import { Tile as TileLayer, Vector as VectorLayer } from 'ol/layer';
import { OSM, XYZ, Vector as VectorSource  } from 'ol/source';
import WMTSTileGrid from 'ol/tilegrid/WMTS'; // For NASA GIBS WMTS Tile Grid
import { getWidth } from 'ol/extent';
import { GeoJSON } from 'ol/format';
import { View } from 'ol'; // Import the View class for map view
import { fromLonLat,toLonLat, get as getProjection, transformExtent } from 'ol/proj'; // For projection

import { Style, Circle as CircleStyle, Fill, Stroke } from 'ol/style'; // For point styling
import Overlay from 'ol/Overlay'; // For the popup
import { Box, Button, CircularProgress, IconButton, Paper, Typography } from '@mui/material';
import LayerSwitcher from './LayerSwitcher'; // Import the LayerSwitcher component
import SearchDrawer from './SearchDrawer'; // Import the SearchDrawer component
import GoogleMapsLikeSearchOverlay from './GoogleLikeSearchOverlay';
import CloseIcon from '@mui/icons-material/Close';
import {  useLocation } from 'react-router-dom';

const MapComponent = () => {
  const { map } = useContext(MapContext); // Access the map object from context
  const mapElementRef = useRef(null);
  const location = useLocation();
  const popupRef = useRef(); // For the popup
  const [popupContent, setPopupContent] = useState(null); // Store content for popup
  const [selectedLayer, setSelectedLayer] = useState('osm'); // Default to satellite
  const [drawerOpen, setDrawerOpen] = useState(false); // Manage drawer state
  const [searchResults, setSearchResults] = useState([]); // Store search results
  const [selectedFeature, setSelectedFeature] = useState(null); // Store selected feature
  const [highlightedResultId, setHighlightedResultId] = useState(null); // Track the highlighted search result
  const [searchQuery, setSearchQuery] = useState(""); // Store the search query
  const [popupPosition, setPopupPosition] = useState(null); // Track popup position for showing/hiding
  const [loading, setLoading] = useState(true); // State to track loading

  // Create layer references
  const [layers, setLayers] = useState({
    satellite: null,
    osm: null,
    geojsonLayer: null
  });
// Style for the selected feature
const selectedStyle = new Style({
    image: new CircleStyle({
      radius: 10,
      fill: new Fill({ color: 'rgba(0, 255, 0, 0.8)' }), // Green for selected
      stroke: new Stroke({
        color: 'black',
        width: 2,
      }),
    }),
  });

  // Default style for other features
  const defaultStyle = new Style({
    image: new CircleStyle({
      radius: 6,
      fill: new Fill({ color: 'rgba(255, 0, 0, 0.8)' }), // Red for default
      stroke: new Stroke({
        color: 'black',
        width: 2,
      }),
    }),
  });
  
  useEffect(() => {
    if (map && mapElementRef.current) {
      // Set the map target to the map element div
      map.setTarget(mapElementRef.current);
        // Set the initial view to Guam (lon/lat: 144.7937, 13.4443)
        const initialView = new View({
            center: fromLonLat([144.7937, 13.4443]), // Center the map on Guam
            zoom: 11, // Zoom level for Guam
        });

        map.setView(initialView); // Apply the view to the map
      // Add Satellite Layer from MapTiler
      const satelliteLayer = new TileLayer({
        source: new XYZ({
          url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', // Replace with your MapTiler API key
          maxZoom: 20,
        }),
        visible: false, // Default satellite visible
      });

      // Add the satellite layer to the map
      map.addLayer(satelliteLayer);
       

      // Add OSM Layer for the normal map view
      const osmLayer = new TileLayer({
        source: new OSM(),
        visible: true, // Initially, OSM layer is hidden
      });

      // Add the OSM layer to the map
      map.addLayer(osmLayer);
     
  loadGeoJSON();
    // Popup overlay for selected features
    const overlay = new Overlay({
        id: 'popup', 
        element: popupRef.current,
        autoPan: false,
        autoPanAnimation: { duration: 250 },
      });
      map.addOverlay(overlay);
      // Store the layers in state for easy access
      setLayers({
        satellite: satelliteLayer,
        osm: osmLayer
      });
  
    }
    
  }, [map, location.search]);
  // Filter the GeoJSON features based on search query
  useEffect(() => {
    if (layers.geojsonLayer) {
      const features = layers.geojsonLayer.getSource().getFeatures();
      const filteredResults = features
        .map((feature) => feature.getProperties())
        .filter(
          (feature) =>
            feature.Name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            feature.BRANCHSERV.toLowerCase().includes(searchQuery.toLowerCase())
        );
      setSearchResults(filteredResults); // Update the filtered search results
      setLoading(false);
    }
  }, [searchQuery, layers.geojsonLayer]);
  // Function to load GeoJSON data from a file
function loadGeoJSON() {
    fetch('../data/cemetery.geojson') // Specify the path to your GeoJSON file
      .then(response => response.json())
      .then(data => {
        // Create vector source from GeoJSON data
        const vectorSource = new VectorSource({
          features: new GeoJSON().readFeatures(data, {
            dataProjection: 'EPSG:3857', // Assuming your GeoJSON is in EPSG:4326
            featureProjection: map.getView().getProjection(), // Map's projection
          })
        });
  
        // Create vector layer and style for the GeoJSON
        const vectorLayer = new VectorLayer({
          source: vectorSource,
          style: defaultStyle
        });
  
        // Add the vector layer to the map
        map.addLayer(vectorLayer);
        setLayers({...layers,geojsonLayer: vectorLayer});
         // Get the extent of the features in the vector source
        const extent = vectorSource.getExtent();
              // Parse lat, lng, and feature ID from the URL query parameters
        const params = new URLSearchParams(location.search);
        const lat = parseFloat(params.get('lat'));
        const lng = parseFloat(params.get('lng'));
        const featureId = params.get('feature');

        // If lat, lng are present in the URL, zoom to that location
        if (lat && lng) {
          const coordinates = fromLonLat([lng, lat]); // Convert lat/lng to map projection
          map.getView().setCenter(coordinates);
          map.getView().setZoom(22); // Zoom to the feature
          // If a feature ID is provided, highlight the feature and show the popup
          if (featureId && vectorLayer) {
            const features = vectorLayer.getSource().getFeatures();
            const selectedFeature = features.find((feature) => feature.get('OBJECTID') === parseInt(featureId, 10));
         
            if (selectedFeature) {
              setSelectedFeature(selectedFeature); // Highlight the feature
              showPopup(coordinates, selectedFeature.getProperties()); // Show popup
            }
          }
        } else {
          // Fit the map's view to the extent of the GeoJSON data
          map.getView().fit(extent, {
            size: map.getSize(),
            maxZoom: 18, // Optional: set a maximum zoom level
        });
        }

        
            setLoading(false); // Hide loader after fetching
      })
      .catch(error => {
        console.error('Error loading GeoJSON file:', error);
      });
  }
  useEffect(()=>{
 // Call the function to load GeoJSON data
 loadGeoJSON();
  },[map])
 
 // useEffect to run when the geojsonLayer is available
 useEffect(() => {
    if (layers.geojsonLayer) {
      // Listen for map extent changes (panning or zooming)
    //   map.on('moveend', () => {
    //     const extent = map.getView().calculateExtent(map.getSize()); // Get current extent
    //     handleMapExtentChange(extent); // Call function to update results based on extent
    //   });
      map.on('singleclick', (event) => {
        map.forEachFeatureAtPixel(event.pixel, (feature) => {
            const featureId = feature.get('OBJECTID');
          setSelectedFeature(feature); // Highlight the feature
          
          setHighlightedResultId(featureId); // Highlight the corresponding search result
          toggleDrawer(true);
          showPopup(event.coordinate, feature.getProperties()); // Show the popup
          const properties = feature.getProperties();
          handleSearchResultClick(properties.OBJECTID); // Highlight the search result item
        });
      });
    }
  }, [layers.geojsonLayer]);
    // Use effect to update feature styles when a new feature is selected
    useEffect(() => {
        if (selectedFeature && layers.geojsonLayer) {
          layers.geojsonLayer.getSource().getFeatures().forEach((feature) => {
            feature.setStyle(feature === selectedFeature ? selectedStyle : defaultStyle);
          });
        }
      }, [selectedFeature, layers.geojsonLayer]);
    
  const showPopup = (coordinates, properties) => {
    const [lng, lat] = toLonLat(coordinates); // Convert map coordinates to lat/lng
    const featureId = properties.OBJECTID;

    // Generate the shareable URL
    const shareableURL = `${window.location.origin}${window.location.pathname}?lat=${lat.toFixed(6)}&lng=${lng.toFixed(6)}&feature=${featureId}`;

    setPopupContent({
      ...properties,
      shareableURL: shareableURL, // Add the shareable URL to the popup content
    }); // Set popup content
    const overlay = map.getOverlayById('popup');
    overlay.setPosition(coordinates); // Set popup position
    setPopupPosition(coordinates); // Track the popup position for visibility
     
  };
  // Hide the popup
  const handleClose = () => {
    setPopupPosition(null); // Hide the popup by setting position to null
  };
  // Handle layer switch
  const handleLayerChange = (event) => {
    const selected = event.target.value;
    setSelectedLayer(selected);

    // Switch between the satellite and OSM layers
    if (layers.satellite && layers.osm) {
      layers.satellite.setVisible(selected === 'satellite');
      layers.osm.setVisible(selected === 'osm');
    }
  };
// Handle extent change and filter search results based on extent
const handleMapExtentChange = (extent) => {
    console.log(layers.geojsonLayer ,'layers.geojsonLayer ');
    if(layers.geojsonLayer == null){
return;
    }

   
    const featuresInView = layers.geojsonLayer
      .getSource()
      .getFeaturesInExtent(extent); // Get features within the current extent

    // Extract properties of features to display in the drawer
    const results = featuresInView.map((feature) => feature.getProperties());
    setSearchResults(results); // Update the state with search results
  };
 // Highlight the feature and navigate to it when search result is clicked
 const handleSearchResultClick = (id) => {
    const features = layers.geojsonLayer.getSource().getFeatures();
    const selectedFeature = features.find((feature) => feature.get('OBJECTID') === id);
    if (selectedFeature) {
      const geometry = selectedFeature.getGeometry();
      const coordinates = geometry.getCoordinates();
      map.getView().setCenter(coordinates);
      map.getView().setZoom(22); // Zoom to the selected feature
      setSelectedFeature(selectedFeature); // Store the selected feature
      showPopup(coordinates, selectedFeature.getProperties()); // Show the popup
    }
  };
  // Toggle drawer
  const toggleDrawer = (open) => () => {
    setDrawerOpen(open);
  };

  return (
    <>
     {/* Loader Overlay */}
     {loading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.7)', // Slight background overlay
            zIndex: 1000, // Ensure loader is on top of the map
          }}
        >
          <CircularProgress />
        </Box>)
}
      <GoogleMapsLikeSearchOverlay loading={loading} setLoading={setLoading} searchResults={searchResults} setSearchQuery={setSearchQuery} searchQuery={searchQuery} onSearchResultClick={handleSearchResultClick} />

      {/* LayerSwitcher Component */}
      <LayerSwitcher
        selectedLayer={selectedLayer}
        handleLayerChange={handleLayerChange}
        drawerOpen={drawerOpen} // Pass the drawer state to shift the LayerSwitcher if needed
      />

      {/* Map container */}
      <Box
        ref={mapElementRef}
        sx={{
          width: '100%',
          height: '100vh', // Full screen height
        }}
      />
      {/* Popup for selected feature */}
      {/* Popup container styled with MUI */}
      <Box
        ref={popupRef}
        className="ol-popup"
        sx={{
          position: 'absolute',
         transform: 'translate(-38%, -10%)', // Position the popup correctly
          display: popupPosition ? 'block' : 'none', // Show only when there is a position
          zIndex: 1000,
          width:'400px'
        }}
      >
        <Paper
          elevation={0}
          sx={{
            padding: '16px',
            maxWidth: '400px',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            borderRadius: '8px'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{popupContent?.Name}</Typography>
            <IconButton size="small" onClick={handleClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Typography variant="body2" color="textSecondary">
                    {popupContent?.BRANCHSERV}
                    {`DOB: ${popupContent?.DOB}, DOD: ${popupContent?.DOD}`}
          </Typography>
          {/* Display the generated URL */}
    {popupContent?.shareableURL && (
      <Box mt={2}>
        <Typography variant="body2" color="textSecondary">
          Shareable Link:
        </Typography>
        <Typography variant="body2" color="primary">
          <a href={popupContent.shareableURL} target="_blank" rel="noopener noreferrer">
            {popupContent.shareableURL}
          </a>
        </Typography>

        {/* Optionally, add a "Copy to Clipboard" button */}
        <Button 
          size="small"
          variant="outlined"
          onClick={() => navigator.clipboard.writeText(popupContent.shareableURL)}
          sx={{ marginTop: '8px' }}
        >
          Copy Link
        </Button>
      </Box>
    )}
        </Paper>
      </Box>
    </>
  );
};

export default MapComponent;
